import {
    makeStyles,
    mergeClasses,
    Tree,
    TreeCheckedChangeData,
    TreeCheckedChangeEvent,
    TreeItem,
    TreeItemLayout,
    TreeOpenChangeData,
    TreeOpenChangeEvent,
    TreeSelectionValue,
} from '@fluentui/react-components';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getPrintReportModalNodeKey } from '../../data-types/printReportModalTOCItem';
import { TaxReturnTOCItem } from '../../model';
import { DefaultTooltipHostV9 } from './DefaultTooltipHostV9';

export interface SelectableFormTOCTreeViewProps {
    taxReturnItems: TaxReturnTOCItem[];
    noRecordsMessage: string;
    openNodeIds: Set<string>;
    checkedStateById: Map<string, boolean | 'mixed'>;
    onOpenNodeIdsChange: (openNodeIds: Set<string>) => void;
    onCheckedStateChange: (nodeId: string, checked: boolean | 'mixed') => void;
    className?: string;
}

interface SelectableFormTOCTreeProps {
    item: TaxReturnTOCItem;
    level: number;
    nodePath: string;
    className?: string;
}

const useStyles = makeStyles({
    noRecordsLabel: {
        margin: '20px',
        fontSize: '14px',
    },
    tree: {
        width: '100%',
        minWidth: 0,
    },
    subtree: {
        width: '100%',
        minWidth: 0,
    },
    treeItemMain: {
        display: 'flex',
        width: '100%',
        minWidth: 0,
    },
    treeItemContent: {
        display: 'flex',
        flex: '1 1 auto',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
    },
    textTruncation: {
        display: 'block',
        flex: '1 1 auto',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
    },
    rightPadding: {
        paddingRight: '8px',
    },
});

const SelectableFormTOCTreeNode: React.FC<SelectableFormTOCTreeProps> = ({
    item,
    level,
    nodePath,
    className,
}: SelectableFormTOCTreeProps) => {
    const itemNodeKey: string | undefined = getPrintReportModalNodeKey(item);
    const styles = useStyles();
    if (!itemNodeKey) {
        return null;
    }
    const childItems: TaxReturnTOCItem[] = item.children ?? [];
    const hasChildren: boolean = childItems.length > 0;
    const itemTitle: string = item.title ?? '';

    return (
        <TreeItem className={className} key={nodePath} value={itemNodeKey} itemType={hasChildren ? 'branch' : 'leaf'}>
            <TreeItemLayout main={{ className: styles.treeItemMain }} selector={{ input: { 'aria-hidden': undefined } }}>
                <div className={styles.treeItemContent}>
                    <DefaultTooltipHostV9
                        containerClassName={styles.textTruncation}
                        appearance='inverted'
                        withArrow
                        positioning='after'
                        content={itemTitle}
                    >
                        <span>{itemTitle}</span>
                    </DefaultTooltipHostV9>
                </div>
            </TreeItemLayout>
            {hasChildren && (
                <Tree className={styles.subtree}>
                    {childItems
                        .filter((childItem) => childItem.isVisibleInPrintView ?? false)
                        .map((childItem: TaxReturnTOCItem, childIndex: number) => {
                            const childNodePath: string = `${nodePath}-child-${childIndex}`;
                            return (
                                <SelectableFormTOCTreeNode
                                    key={childNodePath}
                                    item={childItem}
                                    level={level + 1}
                                    nodePath={childNodePath}
                                />
                            );
                        })}
                </Tree>
            )}
        </TreeItem>
    );
};

const SelectableFormTOCTreeView: React.FC<SelectableFormTOCTreeViewProps> = ({
    taxReturnItems,
    noRecordsMessage,
    openNodeIds,
    checkedStateById,
    onOpenNodeIdsChange,
    onCheckedStateChange,
    className,
}) => {
    const { t } = useTranslation();
    const styles = useStyles();

    const checkedItems: Array<[string, TreeSelectionValue]> = useMemo(() => {
        const nextCheckedItems: Array<[string, TreeSelectionValue]> = [];

        for (const [nodeId, checkedState] of checkedStateById.entries()) {
            const isChecked: boolean = checkedState === true;
            const isMixed: boolean = checkedState === 'mixed';

            if (isChecked || isMixed) {
                nextCheckedItems.push([nodeId, checkedState]);
            }
        }

        return nextCheckedItems;
    }, [checkedStateById]);

    const handleTreeOpenChange = (_event: TreeOpenChangeEvent, data: TreeOpenChangeData): void => {
        const nextOpenNodeIds: Set<string> = new Set<string>();

        for (const openItem of data.openItems) {
            const openItemId: string = openItem.toString();
            nextOpenNodeIds.add(openItemId);
        }

        onOpenNodeIdsChange(nextOpenNodeIds);
    };

    const handleTreeCheckedChange = (_event: TreeCheckedChangeEvent, data: TreeCheckedChangeData): void => {
        const nodeId: string = data.value.toString();
        onCheckedStateChange(nodeId, data.checked);
    };

    const hasNoContent: boolean = taxReturnItems.length === 0;
    if (hasNoContent) {
        return <span className={styles.noRecordsLabel}>{noRecordsMessage}</span>;
    }

    return (
        <Tree
            aria-label={t('formsTableOfContents').toString()}
            openItems={Array.from(openNodeIds)}
            onOpenChange={handleTreeOpenChange}
            selectionMode='multiselect'
            checkedItems={checkedItems}
            onCheckedChange={handleTreeCheckedChange}
            className={mergeClasses(styles.tree, className)}
        >
            {taxReturnItems
                .filter((rootItem: TaxReturnTOCItem) => rootItem.isVisibleInPrintView ?? false)
                .map((rootItem: TaxReturnTOCItem, rootIndex: number) => {
                    const rootNodePath: string = `root-${rootIndex}`;
                    return (
                        <SelectableFormTOCTreeNode
                            key={rootNodePath}
                            item={rootItem}
                            level={0}
                            nodePath={rootNodePath}
                            className={styles.rightPadding}
                        />
                    );
                })}
        </Tree>
    );
};

export default SelectableFormTOCTreeView;
